import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchServices } from "../../../components/global-entities/globalEntitiesSlice";
import { fetchMilestones } from "../actions/milestoneSlice";
import { fetchProccessId } from "../actions/processSlice";
import {
  fetchAttachments,
  setAttachmentData,
} from "../actions/attachmentsSlice";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import { toZonedTime } from "date-fns-tz";
import { getSasToken, getScopedUploadToken } from "~/utils/fileUtils";
import { BlobServiceClient } from "@azure/storage-blob";
import Config, { CURRENT_ENVIRONMENT } from "~/config/env";
import useFileUpload from "~/utils/useFileUpload";
import { z } from "zod";
import useDateTime from "~/hooks/useDateTime";
import { getLspDetails } from "~/Pages/Lsp/actions/lspSlice";
import { getEntityAcess } from "~/components/settings/users/invite-modal/InviteModalSlice";
import { CATEGORIES } from "~/utils/categoryUtils";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";

function useProcess() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { formatDateTime } = useDateTime();
  const [milstoneLoding, setMilestoneLoad] = useState(false);
  const { entityservices } = useSelector((state) => state.globalEntities);
  const { processmilestones } = useSelector((state) => state.milestones);
  const { processdata } = useSelector((state) => state.process);
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [updatedMilestones, setUpdatedMilestones] = useState([]);
  const { id: userId, role } = useSelector((state) => state.login);
  const {
    service_url_override: userServiceUrlOverride,
    payslip_portal_link: userPayslipPortalLink,
  } = useSelector((state) => state.inviteusers);
  const [payslipFiles, setPayslipFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isPayslipProcessingDone, setIsPayslipProcessingDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [paySlipMatchingResult, setPaySlipMatchingResult] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [payslipUrl, setPayslipUrl] = useState(null);
  const { lspdetails } = useSelector((state) => state.lsponboarding);
  const errorToastRef = useRef(null);

  const storageAccount = Config.BLOB_STORAGE_ACCOUNT;
  const conatainerName = Config.BLOB_TEMPORARY_CONTAINER;
  const fileSupported = ["pdf"];
  const { uuidToUlid, updateTag, SERVICE_CODES } = useFileUpload();

  const [statesAttachment, setStatesAttachment] = useState({
    attachmentConfirmModal: false,
    temporaryMessage: "",
    isAttachmentLoader: false,
    selectedFile: {},
    successMessage: "",
    restoreMessage: "",
  });

  const [showAttachmentConfirmation, setShowAttachmentConfirmation] =
    useState(false);
  const [filterData, setFilterData] = useState({});
  const [chipdata, setChipData] = useState({});
  const removeFnRef = useRef(null);
  const {
    data,
    loading: filterLoading,
    error,
    onFilterChange,
    onFilterApplyForProcess,
    onSortOrderChange,
    allData,
    onPageChange: filterPageChange,
    currentPage,
  } = useAttachmentFilter();

  useEffect(() => {
    fetchProcessById();
    fetchProcessMilestones();
    dispatch(fetchServices());
  }, [id]);

  const fetchProcessById = async () => {
    dispatch(fetchProccessId(id));
  };
  const fetchProcessMilestones = async () => {
    setMilestoneLoad(true);
    dispatch(fetchMilestones(id)).then(() => {
      setMilestoneLoad(false);
    });
  };

  useEffect(() => {
    if (!processmilestones) {
      setUpdatedMilestones(undefined);
      return;
    }

    const isPayrollPlusProcess = processdata?.serviceData?.name === "Payroll +";
    const templateMilestones = processdata?.templateData?.template ?? [];

    let sorted;

    if (isPayrollPlusProcess) {
      // Payroll + rule: milestones AND their tasks ordered by ID (ascending)
      // to match the payslip webhook sync task ordering
      sorted = [...processmilestones]
        .sort((a, b) => String(a.id ?? "").localeCompare(String(b.id ?? "")))
        .map((milestone) => ({
          ...milestone,
          taskData: milestone.taskData
            ? [...milestone.taskData].sort((a, b) =>
                String(a.id ?? "").localeCompare(String(b.id ?? "")),
              )
            : milestone.taskData,
        }));
    } else if (templateMilestones.length > 0) {
      // All other processes: sort milestones by their position in the workflow
      // template configuration — date modifications must NOT affect order
      const templateOrderMap = new Map(
        templateMilestones.map((m, index) => [m.name, index]),
      );
      sorted = [...processmilestones].sort((a, b) => {
        const indexA = templateOrderMap.has(a.name)
          ? templateOrderMap.get(a.name)
          : Infinity;
        const indexB = templateOrderMap.has(b.name)
          ? templateOrderMap.get(b.name)
          : Infinity;
        return indexA - indexB;
      });
    } else {
      // templateData not yet available — preserve API order until it arrives
      sorted = [...processmilestones];
    }

    const updated = sorted.map((milestone) => ({
      ...milestone,
      due_date: toZonedTime(
        milestone.due_date,
        processdata?.entityData?.timezone,
      ),
    }));
    setUpdatedMilestones(updated);
  }, [
    processmilestones,
    processdata?.entityData?.timezone,
    processdata?.templateData?.template, // re-sort when template data arrives
    processdata?.serviceData?.name, // re-sort when service type is known
  ]);

  const fetchProcessAttachments = async (skipCache = false) => {
    dispatch(setAttachmentData({ key: "loadingAttachments", value: true }));
    dispatch(setAttachmentData({ key: "processAttachments", value: [] }));
    setMilestoneLoad(true);
    dispatch(
      fetchAttachments({
        id,
        process: processdata?.id,
        entity: processdata?.entity,
        skipCache,
      }),
    ).then(() => {
      setMilestoneLoad(false);
    });
  };

  const formatDate = (dateString) => {
    if (dateString) {
      return formatDateTime({
        date: dateString,
        dateformat: "MMMM do, yyyy",
      });
    } else {
      ("-");
    }
  };

  const hideRequestModal = () => {
    setShowRequestModal(false);
  };

  const handleDeleteDocument = (file) => {
    setStatesAttachment((prev) => ({
      ...prev,
      attachmentConfirmModal: true,
      selectedFile: file,
    }));
  };
  const handleConfirmDeleteDocumment = useCallback(async () => {
    setStatesAttachment((prev) => ({
      ...prev,
      isAttachmentLoader: true,
      restoreMessage: "",
      temporaryMessage: "",
    }));

    try {
      const { status } = await apiService(
        API_ROUTES.DELETE_ATTACHMENT,
        "DELETE",
        {
          body: {
            blobUrl: statesAttachment?.selectedFile?.blob_url,
            name: statesAttachment?.selectedFile?.name,
          },
          replace: { relatedId: id, relatedType: "PROCESS" },
        },
      );

      if (status) {
        setTimeout(async () => {
          try {
            if (selectedItems?.length) {
              await onFilterApplyForProcess(["PROCESS", "PAYSLIPS"]);
            } else {
              await fetchProcessAttachments(true); // GXM-1753: skipCache after delete
            }
            setStatesAttachment((prev) => ({
              ...prev,
              temporaryMessage: `${statesAttachment?.selectedFile?.displayName || statesAttachment?.selectedFile?.name} has been deleted.`,
              attachmentConfirmModal: false,
              isAttachmentLoader: false,
            }));
          } catch (error) {
            // GXM-1753: Reset loader on fetch error to prevent infinite loading
            console.error("Error refreshing attachments after delete:", error);
            setStatesAttachment((prev) => ({
              ...prev,
              attachmentConfirmModal: false,
              isAttachmentLoader: false,
            }));
          }
        }, 2000);
      } else {
        // GXM-1753: Reset loader if delete fails
        setStatesAttachment((prev) => ({
          ...prev,
          attachmentConfirmModal: false,
          isAttachmentLoader: false,
        }));
      }
    } catch (error) {
      // GXM-1753: Reset loader on API error
      console.error("Error deleting attachment:", error);
      setStatesAttachment((prev) => ({
        ...prev,
        attachmentConfirmModal: false,
        isAttachmentLoader: false,
      }));
    }
  }, [statesAttachment?.selectedFile, fetchProcessAttachments]); // Dependencies

  const handleDeletePayslip = useCallback(
    async (matchingResultId) => {
      setStatesAttachment((prev) => ({
        ...prev,
        isAttachmentLoader: true,
        restoreMessage: "",
        temporaryMessage: "",
      }));
      try {
        setIsLoading(true);
        const { status } = await apiService(
          API_ROUTES.DELETE_ATTACHMENT,
          "DELETE",
          {
            body: {
              blobUrl: statesAttachment?.selectedFile?.blob_url,
              name: statesAttachment?.selectedFile?.name,
              container: statesAttachment?.selectedFile?.blob_url?.includes(
                "/ess",
              )
                ? "ess"
                : "documents",
            },
            replace: { relatedId: id, relatedType: "PROCESS" },
          },
        );
        // GXM-1747: Refresh UI after successful delete
        setTimeout(async () => {
          try {
            if (selectedItems?.length) {
              await onFilterApplyForProcess(["PROCESS", "PAYSLIPS"]);
            } else {
              await fetchReleasedPayslips();
              await fetchProcessAttachments(true); // GXM-1753: skipCache after delete
            }
            setStatesAttachment((prev) => ({
              ...prev,
              temporaryMessage: `${decodeURIComponent(statesAttachment?.selectedFile?.name)} has been deleted.`,
              attachmentConfirmModal: false,
              isAttachmentLoader: false,
            }));
          } catch (error) {
            // GXM-1753: Reset loader on fetch error to prevent infinite loading
            console.error("Error refreshing after payslip delete:", error);
            setStatesAttachment((prev) => ({
              ...prev,
              attachmentConfirmModal: false,
              isAttachmentLoader: false,
            }));
          }
        }, 2000);
      } catch (error) {
        console.log("Error deleting payslip", error);
        // GXM-1747: Reset UI state on error so user can retry
        setStatesAttachment((prev) => ({
          ...prev,
          attachmentConfirmModal: false,
          isAttachmentLoader: false,
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [statesAttachment?.selectedFile, fetchProcessAttachments],
  );
  const handleUndoPayslip = async () => {
    try {
      setStatesAttachment((prev) => ({
        ...prev,
        temporaryMessage: "",
        restoreMessage: "",
      }));
      dispatch(
        setAttachmentData({
          key: "loadingAttachments",
          value: true,
        }),
      );
      const { status } = await apiService(
        API_ROUTES.RESTORE_ATTACHMENT,
        "POST",
        {
          body: {
            blobUrl: statesAttachment?.selectedFile?.blob_url,
            container: `ess`,
          },
        },
      );
      if (status) {
        setTimeout(async () => {
          try {
            if (selectedItems?.length) {
              await onFilterApplyForProcess(["PROCESS", "PAYSLIPS"]);
            } else {
              await fetchReleasedPayslips();
              await fetchProcessAttachments(true); // GXM-1753: skipCache after restore
            }
            setStatesAttachment((prev) => ({
              ...prev,
              restoreMessage: `${decodeURIComponent(statesAttachment?.selectedFile?.name)} has been restored.`,
            }));
          } catch (error) {
            // GXM-1753: Reset loader on fetch error
            console.error("Error refreshing after payslip restore:", error);
          }
        }, 2000);
      }
    } catch (error) {
      console.log("Payslip Undo failed", error);
    }
  };

  const handleRestoreDocument = useCallback(async () => {
    setStatesAttachment((prev) => ({
      ...prev,
      temporaryMessage: "",
      restoreMessage: "",
    }));
    dispatch(
      setAttachmentData({
        key: "loadingAttachments",
        value: true,
      }),
    );
    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: {
        blobUrl: statesAttachment?.selectedFile?.blob_url,
        name: statesAttachment?.selectedFile?.name,
      },
    });

    if (status) {
      setTimeout(async () => {
        try {
          if (selectedItems?.length) {
            await onFilterApplyForProcess(["PROCESS", "PAYSLIPS"]);
          } else {
            await fetchProcessAttachments(true); // skipCache after restore
          }
          setStatesAttachment((prev) => ({
            ...prev,
            restoreMessage: `${statesAttachment?.selectedFile?.displayName || statesAttachment?.selectedFile?.name} has been restored.`,
          }));
        } catch (error) {
          // GXM-1753: Reset loader on fetch error
          console.error("Error refreshing after document restore:", error);
        }
      }, 2000);
    }
  }, [statesAttachment?.selectedFile, fetchProcessAttachments]);

  const addPayslipMatchingResults = async (updatedAttachments, messageId) => {
    await Promise.all(
      updatedAttachments.forEach(async (data) => {
        await apiService(API_ROUTES.ADD_PAYSLIP_MATCHING_RESULTS, "POST", {
          body: {
            processId: id,
            file_reference: data?.url,
            match_status: "",
            user_id: userId,
            messageId,
            extracted_data: {
              original_filename: data?.fileName,
              payslipId: data?.payslipId,
              fileSize: data?.fileSize,
            },
          },
          schema: z.object({
            processId: z.string().uuid(),
            file_reference: z.string().url(),
            match_status: z.string(),
            user_id: z.string().uuid(),
            messageId: z.string().uuid(),
            extracted_data: z.object({
              original_filename: z.string(),
              payslipId: z.string().uuid(),
              fileSize: z.string(),
            }),
          }),
        });
      }),
    );
  };

  const uploadPaylispFiles = async (filesData, fileType) => {
    setIsPayslipProcessingDone(false);
    const results = await Promise.allSettled(
      filesData.map(async (fileData) => {
        const file = fileData.file;
        const uuid = crypto.randomUUID();
        const metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: encodeURIComponent(file.name),
          created_at: new Date().toISOString(),
          file_object_type: "PAYSLIP",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };
        const category = CATEGORIES.find(
          (data) => data.code === "PAYSLIPS_AND_PAYROLL_REPORTS",
        );
        const subcategory = category.subCategory.find(
          (data) => data.code === "PAYSLIPS",
        );
        const tags = {
          process: uuidToUlid(id),
          payslip: uuidToUlid(uuid),
          isPublic: "false",
          category: category.code,
          subCategory: subcategory.code,
          year: new Date().getFullYear().toString(),
        };
        const scopeServiceCode =
          SERVICE_CODES[processdata?.serviceData?.name] || "PAYROLL";
        const tokenData = await getScopedUploadToken(conatainerName, {
          entityId: processdata?.entity,
          serviceLineCode: scopeServiceCode,
          fileName: file.name,
        });
        const serverBlobName = tokenData.blobName;
        const uploadUrl = tokenData.blobUrl;

        // PUT the file directly to the server-provided blobUrl
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }

        const baseUrl = uploadUrl.split("?")[0];
        await updateTag(serverBlobName, tags, metadata);
        return {
          url: normalizeFileReferenceUrl(extractFileUrl(baseUrl)),
          fileName: serverBlobName,
          payslipId: uuid,
          fileSize: file.size.toString(),
        };
      }),
    );

    const successfulUploads = results
      .filter((r) => r.status === "fulfilled")
      .map((r) => r.value);

    const updatedAttachments = [
      ...attachments.filter(
        (existing) =>
          !successfulUploads.some((s) => s.fileName === existing.fileName),
      ),
      ...successfulUploads,
    ];
    setAttachments(updatedAttachments);

    const allUploaded = payslipFiles.every((original) =>
      updatedAttachments.some((a) => a.fileName === original.file.name),
    );

    if (allUploaded) {
      // add one messageId for files
      const messageId = crypto.randomUUID();
      addPayslipMatchingResults(updatedAttachments, messageId);
      addPayslipProcessingMessage(updatedAttachments);
      await triggerPayslipMatchingResults(
        updatedAttachments,
        fileType,
        messageId,
      );
      return true;
    }

    setIsLoading(false);
    return false;
  };
  const triggerPayslipMatchingResults = async (
    file_reference,
    fileType,
    messageId,
  ) => {
    const taskData =
      processmilestones
        .flatMap((milestone) => milestone.taskData || [])
        .find((task) => task.name === "Submit final output to Customer") ||
      null;
    try {
      await apiService(API_ROUTES.TRIGGER_PAYSLIP_MATCHING_RESULTS, "POST", {
        body: {
          taskId: taskData?.id,
          processId: id,
          file_reference,
          split: fileType === "single",
          messageId,
        },
        schema: z.object({
          taskId: z.string().uuid(),
          processId: z.string().uuid(),
          messageId: z.string().uuid(),
          file_reference: z.array(
            z.object({
              fileName: z.string(),
              url: z.string(),
            }),
          ),
          split: z.boolean(),
        }),
      });
    } catch (error) {
      console.error("Error triggering payslip matching results:", error);
    }
  };

  const extractFileUrl = (url) => {
    return url.split("?")[0];
  };

  const normalizeFileReferenceUrl = (url) => {
    const parsed = new URL(url);
    const decodedPath = decodeURIComponent(parsed.pathname);
    const normalizedPath = decodedPath
      .split("/")
      .map((segment) =>
        encodeURIComponent(segment)
          .replace(/'/g, "%27")
          .replace(/\(/g, "%28")
          .replace(/\)/g, "%29"),
      )
      .join("/");

    return `${parsed.protocol}//${parsed.host}${normalizedPath}`;
  };

  const getBlockBlobClientPayslip = async (blobName, uuid) => {
    const tenantId = processdata.tenant_id;
    blobName = `${tenantId}/${id}/tmp/${uuid}/${blobName}`;
    const BLOB_SAS_URL = `https://${storageAccount}.blob.core.windows.net/${conatainerName}?`;
    const sasToken = await getSasToken(conatainerName);
    const blobUrl = BLOB_SAS_URL + sasToken;
    const blobServiceClient = new BlobServiceClient(blobUrl);
    const containerClient =
      blobServiceClient.getContainerClient(conatainerName);
    return containerClient.getBlockBlobClient(blobName);
  };
  const uploadFile = async (file, uuid, blobName = file.name) => {
    try {
      const blockBlobClient = await getBlockBlobClientPayslip(blobName, uuid);
      await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: {
          blobContentType: file.type,
        },
      });
      return {
        id: blobName,
        url: blockBlobClient.url,
      };
    } catch (error) {
      console.log(`An unexpected error occured : ${error}`);
      setFileServerError(file);
      throw error;
    }
  };

  const tryAgainHandler = async (fileData) => {
    setIsLoading(true);
    await uploadPaylispFiles([fileData]);
  };

  const setFileServerError = (file) => {
    setPayslipFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file.name === file.name &&
        f.file.size === file.size &&
        f.file.lastModified === file.lastModified
          ? {
              ...f,
              error: {
                summary: "File Upload Failed. ",
                detail: "Something went wrong. Please try uploading again: ",
                otherAction: "Try Again.",
              },
            }
          : f,
      ),
    );
  };

  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);

  const addPayslipProcessingMessage = async (attachments) => {
    try {
      const data = await apiService(
        API_ROUTES.ADD_PAYSLIP_PROCESSING_MESSAGE,
        "POST",
        {
          body: {
            comment:
              "We’re currently processing payslips uploaded. You’ll be notified when completed",
            isPublic: false,
            attachments,
            type: "PAYSLIP_PROCESSING",
          },
          replace: {
            relatedId: id,
            relatedType: "PROCESS",
          },
          schema: z.object({
            relatedId: z.string().uuid(),
            relatedType: z.enum(["PROCESS"]),
            comment: z.string(),
            isPublic: z.boolean(),
            attachments: z.array(
              z.object({
                fileName: z.string(),
                url: z.string(),
              }),
            ),
          }),
        },
      );
      setIsPayslipProcessingDone(true);
      return data;
    } catch (error) {
      console.error("Error adding payslip processing message:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setIsLoading(false);
    setPayslipFiles([]);
    setAttachments([]);
    setIsPayslipProcessingDone(false);
  };
  const fetchPayslipMatching = async (status = "", skipCache = false) => {
    setLoading(true);
    const { data } = await apiService(API_ROUTES.PAYSLIP_MATCHING, "GET", {
      replace: { id: id, status: status },
      skipCache,
    });
    if (data?.length) {
      const filteredData = data.filter((d) => d?.match_status !== "rejected");
      if (filteredData?.length) setIsDataAvailable(true);
      else setIsDataAvailable(false);
      setPaySlipMatchingResult(filteredData);
    } else {
      setIsDataAvailable(false);
      setPaySlipMatchingResult([]);
    }
    setLoading(false);
  };
  //fetch payslips from ess container
  const fetchReleasedPayslips = async () => {
    setLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id: id, type: "PAYSLIPS" },
    });
    if (data?.length) {
      const filteredData = data;
      if (filteredData?.length) setIsDataAvailable(true);
      else setIsDataAvailable(false);
      setPaySlipMatchingResult(filteredData);
    } else {
      setIsDataAvailable(false);
      setPaySlipMatchingResult([]);
    }
    setLoading(false);
  };
  const submitPayslipMatchingResult = async (matchedFiles) => {
    try {
      return await apiService(
        API_ROUTES.UPDATE_PAYSLIP_MATCHING_RESULTS,
        "PATCH",
        {
          body: { matchedFiles, updateMessage: false },
        },
      );
    } catch (error) {
      console.error("Error submitting payslip matching result:", error);
      errorToastRef.current.show({
        severity: "error",
        detail: `Database and status update failed. Please review files and retry.`,
        life: 5000,
      });
    }
  };

  const releaseMatchingResult = async (tasksdata) => {
    try {
      const approvedFiles = paySlipMatchingResult
        .filter((data) => data?.match_status === "approved")
        .map((fileItem) => {
          return { ...fileItem, match_status: "released" };
        });
      return await submitPayslipMatchingResult(approvedFiles);
    } catch (error) {
      console.error("Error releasing matching results:", error);
    }
  };
  const fetchEmployees = async () => {
    try {
      const { data } = await apiService(API_ROUTES.FETCH_EMPLOYEES, "GET", {
        replace: {
          id: processdata?.entity,
          page: 1,
          pageSize: 100,
          search: "",
          sortBy: "name",
          sortOrder: "ASC",
        },
        schema: z.object({
          id: z.string().uuid(),
          page: z.number().optional(),
          pageSize: z.number().optional(),
          search: z.string().optional(),
          sortBy: z
            .enum([
              "employee_identifier",
              "name",
              "work_email",
              "job_title",
              "employment_status",
              "start_date",
              "end_date",
            ])
            .optional(),
          sortOrder: z.enum(["ASC", "DESC"]).optional(),
        }),
      });
      const employeeList = data?.records || [];

      if (employeeList.length) {
        setEmployeesData(employeeList);
      } else {
        setEmployeesData([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };
  const isPayrollPlus = processdata?.serviceData?.name === "Payroll +";

  // GXM-1744: Resolve payslip URL based on user's tenant payslip_portal_link
  // All tenant types (LSP/Customer) use payslip_portal_link from their tenant
  // GXM users: use generic payslip URL
  const resolvePayslipUrl = () => {
    const GXM_ROLES = ["GXM_USER", "GXM_MANAGER", "GXM_ADMIN"];
    let payslipURL;
    if (CURRENT_ENVIRONMENT === "STAG" || CURRENT_ENVIRONMENT === "DEV") {
      payslipURL = "https://hsp.pay-preprod.com";
    } else {
      payslipURL = "https://hsp.payslip.com";
    }

    // GXM users get generic URL
    if (GXM_ROLES.includes(role)) {
      setPayslipUrl(payslipURL);
      return;
    }

    // All other users (LSP/Customer): use tenant's payslip_portal_link
    if (role === "LSP_USER" || role === "LSP_MANAGER") {
      setPayslipUrl(userPayslipPortalLink);
      return;
    }

    // Fallback to generic URL if not set
    setPayslipUrl(payslipURL);
  };

  // Fetch user's entity access to get payslip_portal_link
  useEffect(() => {
    const GXM_ROLES = ["GXM_USER", "GXM_MANAGER", "GXM_ADMIN"];
    if (isPayrollPlus && !GXM_ROLES.includes(role) && userId) {
      dispatch(getEntityAcess(userId));
    }
  }, [isPayrollPlus, role, userId, dispatch]);

  useEffect(() => {
    if (isPayrollPlus) {
      resolvePayslipUrl();
    }
  }, [userPayslipPortalLink, role, isPayrollPlus]);

  const handleRemoveFilter = (id) => {
    if (removeFnRef.current) {
      const { value: updatedValue, grouped } = removeFnRef.current(id);
      setFilterData(updatedValue);
      handleFilterChange(grouped);
      if (!Object.keys(updatedValue)?.length) {
        fetchReleasedPayslips();
        fetchProcessAttachments();
      }
    }
  };
  const handleFilterChange = (data) => {
    const categories = Object.entries(data?.category || {}).map(
      ([key, value]) => ({
        category: key,
        subCategories: value?.map((sub) =>
          sub.includes("/") ? sub.split("/").pop() : sub,
        ),
      }),
    );
    setChipData(categories);
    onFilterChange({
      categories,
      typeId: id,
      type: "PROCESS",
    });
  };

  const selectedItems = Object.values(filterData || {}).filter(
    (value) =>
      !["category", "service_line", "entities", "year"].includes(value?.id),
  );
  useEffect(() => {
    if (selectedItems?.length) {
      onFilterApplyForProcess(["PROCESS", "PAYSLIPS"]);
    }
  }, [filterData]);

  // Publish attachment files in Core Entity Info
  const promoteAttachmentsToEntityInfo = async (filePayload) => {
    try {
      setLoading(true);
      return await apiService(API_ROUTES.PROMOTE_ATTACHMENT_TO_ENTITY, "POST", {
        body: filePayload,
        suppressToast: true,
        schema: z.object({
          containerName: z.string(),
          blobName: z.string(),
          category: z.string(),
          subCategory: z.string(),
        }),
      });
    } catch (error) {
      throw new Error("Error promoting attachments to entity info:", error);
    } finally {
      setLoading(false);
    }
  };

  // Publish attachment files in Core Entity Info
  const unpublishAttachmentsFromEntityInfo = async (filePayload) => {
    try {
      setLoading(true);
      return await apiService(API_ROUTES.UNPUBLISH_DOCUMENT, "POST", {
        body: filePayload,
        suppressToast: true,
        schema: z.object({
          containerName: z.string(),
          blobName: z.string(),
        }),
      });
    } catch (error) {
      throw new Error(
        "Error unpublishing attachments from entity info:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };
  return {
    allservices: entityservices,
    process: processdata,
    milestones: updatedMilestones,
    milestonesLoading: milstoneLoding,
    isAuditSidebarOpen,
    setIsAuditSidebarOpen,
    formatDate,
    fetchProcessAttachments,
    fetchProcessById,
    showRequestModal,
    setShowRequestModal,
    hideRequestModal,
    statesAttachment,
    handleDeleteDocument,
    handleConfirmDeleteDocumment,
    handleRestoreDocument,
    setStatesAttachment,
    uploadPaylispFiles,
    payslipFiles,
    setPayslipFiles,
    isLoading,
    setIsLoading,
    fileSupported,
    getFileSizeMB,
    tryAgainHandler,
    showAttachmentConfirmation,
    setShowAttachmentConfirmation,
    isPayslipProcessingDone,
    reset,
    fetchPayslipMatching,
    loading,
    isDataAvailable,
    paySlipMatchingResult,
    releaseMatchingResult,
    employeesData,
    fetchEmployees,
    handleDeletePayslip,
    handleUndoPayslip,
    setLoading,
    setIsDataAvailable,
    setPaySlipMatchingResult,
    isPayrollPlus,
    payslipUrl,
    fetchReleasedPayslips,
    errorToastRef,
    selectedItems,
    filterData,
    setFilterData,
    chipdata,
    setChipData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    data,
    filterLoading,
    error,
    onFilterChange,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
    errorToastRef,
    promoteAttachmentsToEntityInfo,
    unpublishAttachmentsFromEntityInfo,
  };
}

export default useProcess;
